// Dynamic serving-size ingredient scaling.
//
// Non-scalable ("to taste" / "as needed") ingredients are not a new concept:
// the API already represents them with ingredient_amount 0 and a sentinel
// unit_id (0 = "مقدار دلخواه", -1 = "مقدار لازم" - see db/units.py on the
// backend and the `ing.amount !== 0` check this file replaces below), so we
// reuse that convention instead of inventing a parallel flag.
const NON_SCALABLE_UNIT_IDS = new Set([0, -1]);

function isScalableIngredient(ingredient) {
  return (
    ingredient.amount !== null &&
    ingredient.amount !== undefined &&
    ingredient.amount > 0 &&
    !NON_SCALABLE_UNIT_IDS.has(ingredient.unit_id)
  );
}

// overrides: the flat list the API returns ({ingredient_id, servings, amount, unit}).
// Indexed as ingredient_id -> servings -> {amount, unit} for O(1) lookup per render.
function buildOverrideIndex(overrides) {
  const index = new Map();
  (overrides || []).forEach((ov) => {
    if (!index.has(ov.ingredient_id)) index.set(ov.ingredient_id, new Map());
    index.get(ov.ingredient_id).set(ov.servings, { amount: ov.amount, unit: ov.unit });
  });
  return index;
}

// Always scales from the ingredient's original base_quantity (never from a
// previously displayed value), so repeated serving changes can't compound
// rounding error: scaled = base_quantity * selected_servings / base_servings.
function getDisplayIngredient(ingredient, servings, baseServings, overrideIndex) {
  if (!isScalableIngredient(ingredient)) {
    return { amount: null, unit: ingredient.unit, isOverridden: false };
  }

  const override = overrideIndex.get(ingredient.id)?.get(servings);
  if (override) {
    return { amount: override.amount, unit: override.unit, isOverridden: true };
  }

  const scaled = (ingredient.amount * servings) / baseServings;
  return { amount: scaled, unit: ingredient.unit, isOverridden: false };
}

// Cooking-friendly rounding: at most 2 decimals, no trailing zeros (1.50 -> 1.5,
// 2.00 -> 2), while staying numerically accurate (0.333... -> 0.33).
function formatQuantity(amount) {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '';
  const rounded = Math.round((amount + Number.EPSILON) * 100) / 100;
  return rounded.toString();
}

export { isScalableIngredient, buildOverrideIndex, getDisplayIngredient, formatQuantity };
